import { PAYMENT_NETWORK_ACH, PAYMENT_NETWORK_WIRE, type BankPaymentNetwork } from '@klear/bank-account/constants'

export const WIRE_ADDRESS_KEYS = ['line1', 'city', 'state', 'postal_code', 'country'] as const
export type WireAddressKey = (typeof WIRE_ADDRESS_KEYS)[number]

type PartialAddress = Partial<Record<WireAddressKey, string | null | undefined>>

export function missingWireAddressKeys(address?: PartialAddress | null): WireAddressKey[] {
  return WIRE_ADDRESS_KEYS.filter((key) => !address?.[key]?.trim())
}

export function isWireAddressComplete(address?: PartialAddress | null): boolean {
  return missingWireAddressKeys(address).length === 0
}

export const NETWORK_UNAVAILABLE_BANK = 'not_supported_by_bank' as const
export const NETWORK_UNAVAILABLE_ADDRESS = 'missing_billing_address' as const
export const NETWORK_NOT_ELECTED = 'not_elected' as const
export type PaymentNetworkUnavailableReason =
  | typeof NETWORK_UNAVAILABLE_BANK
  | typeof NETWORK_UNAVAILABLE_ADDRESS
  | typeof NETWORK_NOT_ELECTED

export type PaymentNetworkReconciliation = {
  supported: BankPaymentNetwork[]
  achUnavailableReason?: PaymentNetworkUnavailableReason
  wireUnavailableReason?: PaymentNetworkUnavailableReason
}

export function reconcileSupportedPaymentNetworks(
  detected: readonly BankPaymentNetwork[],
  elected: readonly BankPaymentNetwork[],
  hasWireAddress: boolean
): PaymentNetworkReconciliation {
  const result: PaymentNetworkReconciliation = { supported: [] }

  if (!elected.includes(PAYMENT_NETWORK_ACH)) {
    result.achUnavailableReason = NETWORK_NOT_ELECTED
  } else if (!detected.includes(PAYMENT_NETWORK_ACH)) {
    result.achUnavailableReason = NETWORK_UNAVAILABLE_BANK
  } else {
    result.supported.push(PAYMENT_NETWORK_ACH)
  }

  if (!elected.includes(PAYMENT_NETWORK_WIRE)) {
    result.wireUnavailableReason = NETWORK_NOT_ELECTED
  } else if (!detected.includes(PAYMENT_NETWORK_WIRE)) {
    result.wireUnavailableReason = NETWORK_UNAVAILABLE_BANK
  } else if (!hasWireAddress) {
    result.wireUnavailableReason = NETWORK_UNAVAILABLE_ADDRESS
  } else {
    result.supported.push(PAYMENT_NETWORK_WIRE)
  }

  return result
}

export function electedNetworkRejection(
  reconciliation: PaymentNetworkReconciliation
): { network: BankPaymentNetwork; reason: PaymentNetworkUnavailableReason; message: string } | undefined {
  if (reconciliation.wireUnavailableReason && reconciliation.wireUnavailableReason !== NETWORK_NOT_ELECTED) {
    return {
      network: PAYMENT_NETWORK_WIRE,
      reason: reconciliation.wireUnavailableReason,
      message:
        reconciliation.wireUnavailableReason === NETWORK_UNAVAILABLE_ADDRESS
          ? 'Wire transfers need a complete billing address on this account. Add one, or clear the wire option.'
          : 'This routing number does not support wire transfers. Ask your bank for their wire routing number, or clear the wire option.'
    }
  }
  if (reconciliation.achUnavailableReason && reconciliation.achUnavailableReason !== NETWORK_NOT_ELECTED) {
    return {
      network: PAYMENT_NETWORK_ACH,
      reason: reconciliation.achUnavailableReason,
      message:
        'This routing number does not support ACH transfers. If your bank gave you a wire-only routing number, clear the ACH option and use this account for wires.'
    }
  }
  return undefined
}

export function noSupportedNetworkMessage(reconciliation: PaymentNetworkReconciliation): string | undefined {
  if (reconciliation.supported.length > 0) return undefined

  if (
    reconciliation.achUnavailableReason === NETWORK_NOT_ELECTED &&
    reconciliation.wireUnavailableReason === NETWORK_NOT_ELECTED
  ) {
    return 'Select at least one network for this account.'
  }
  if (reconciliation.wireUnavailableReason === NETWORK_UNAVAILABLE_ADDRESS) {
    return 'This routing number only supports wire transfers. Add a billing address to enable wire on this account.'
  }
  return 'This bank account supports neither ACH nor wire transfers. Check the routing number, or contact your bank for one that accepts electronic transfers.'
}

export function toBankPaymentNetworks(stripeNetworks: readonly string[] | null | undefined): BankPaymentNetwork[] {
  const out: BankPaymentNetwork[] = []
  if (stripeNetworks?.includes(PAYMENT_NETWORK_ACH)) out.push(PAYMENT_NETWORK_ACH)
  if (stripeNetworks?.includes(PAYMENT_NETWORK_WIRE)) out.push(PAYMENT_NETWORK_WIRE)
  return out
}
